import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Button,
  TextField
} from '@mui/material'
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams
} from '@mui/x-data-grid'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'
import {
  Download as DownloadIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon
} from '@mui/icons-material'
import { AnalyticsService } from '../../../../shared/services/AnalyticsService'
import type {
  SlaAnalyticsResponse,
  EscalationAnalyticsResponse,
  AtRiskTicketSummary
} from '../../../../shared/types/entities'

function SlaEscalationAnalytics() {
  const [slaData, setSlaData] = useState<SlaAnalyticsResponse | null>(null)
  const [escalationData, setEscalationData] = useState<EscalationAnalyticsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Date range state (default to last 30 days)
  const [startDate, setStartDate] = useState(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  )
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split('T')[0]
  )

  useEffect(() => {
    loadAnalytics()
  }, [startDate, endDate])

  const loadAnalytics = async () => {
    try {
      setLoading(true)
      setError(null)

      const [sla, escalation] = await Promise.all([
        AnalyticsService.getSlaAnalytics(startDate, endDate),
        AnalyticsService.getEscalationAnalytics(startDate, endDate)
      ])

      setSlaData(sla)
      setEscalationData(escalation)
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics')
    } finally {
      setLoading(false)
    }
  }

  const handleExportCSV = () => {
    if (!slaData || !escalationData) return

    // Prepare CSV data
    const csvRows = [
      // SLA Summary
      ['SLA Analytics Summary'],
      ['Metric', 'Value'],
      ['Total Tickets', slaData.totalTickets],
      ['At-Risk Tickets', slaData.atRiskTicketsCount],
      ['Overall Compliance Rate', `${slaData.overallComplianceRate}%`],
      ['Response SLA Breaches', slaData.responseSlaBreaches],
      ['Resolution SLA Breaches', slaData.resolutionSlaBreaches],
      [''],
      // Escalation Summary
      ['Escalation Analytics Summary'],
      ['Metric', 'Value'],
      ['Total Escalations', escalationData.totalEscalations],
      ['Manual Escalations', escalationData.manualEscalations],
      ['Auto-SLA Escalations', escalationData.autoSlaEscalations],
      ['Escalation Rate', `${escalationData.escalationRate}%`],
      ['Avg Escalation Time', `${escalationData.averageEscalationTimeMinutes} minutes`]
    ]

    const csvContent = csvRows.map(row => row.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `sla-escalation-analytics-${startDate}-to-${endDate}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  // DataGrid columns for at-risk tickets
  const atRiskColumns: GridColDef<AtRiskTicketSummary>[] = [
    {
      field: 'riskLevel',
      headerName: 'Risk',
      width: 100,
      renderCell: (params: GridRenderCellParams<AtRiskTicketSummary>) => {
        const color = params.value === 'Critical' ? 'error' :
                     params.value === 'High' ? 'warning' : 'info'
        return <Chip label={params.value} color={color} size="small" />
      }
    },
    { field: 'ticketNumber', headerName: 'Ticket #', width: 120 },
    { field: 'subject', headerName: 'Subject', flex: 1, minWidth: 200 },
    { field: 'priority', headerName: 'Priority', width: 100 },
    { field: 'assignedAgentName', headerName: 'Assigned To', width: 150 },
    {
      field: 'minutesUntilResolutionBreach',
      headerName: 'Time Until Breach',
      width: 160,
      renderCell: (params: GridRenderCellParams<AtRiskTicketSummary>) => {
        const minutes = Math.min(
          params.row.minutesUntilResponseBreach,
          params.row.minutesUntilResolutionBreach
        )
        const hours = Math.floor(minutes / 60)
        const mins = minutes % 60
        return `${hours}h ${mins}m`
      }
    }
  ]

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="info" sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Typography variant="h6" gutterBottom>Not Enough Data Yet</Typography>
        <Typography variant="body2">
          There isn't enough escalation data available for the selected date range to display analytics.
          Analytics will become available once tickets are escalated or SLA breaches occur.
        </Typography>
      </Alert>
    )
  }

  if (!slaData || !escalationData) {
    return (
      <Alert severity="info" sx={{ maxWidth: 600, mx: 'auto', mt: 4 }}>
        <Typography variant="h6" gutterBottom>No Data Available</Typography>
        <Typography variant="body2">
          No SLA or escalation data found for the selected date range. Try adjusting the date range or create some tickets to see analytics.
        </Typography>
      </Alert>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">SLA & Escalation Analytics</Typography>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExportCSV}
        >
          Export CSV
        </Button>
      </Box>

      {/* Date Range Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" gap={2} alignItems="center">
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* SLA Compliance Metrics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <SpeedIcon color="primary" />
                <Typography variant="subtitle2" color="text.secondary">
                  Overall SLA Compliance
                </Typography>
              </Box>
              <Typography variant="h4">
                {slaData.overallComplianceRate.toFixed(1)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {slaData.totalTickets} total tickets
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <WarningIcon color="warning" />
                <Typography variant="subtitle2" color="text.secondary">
                  At-Risk Tickets
                </Typography>
              </Box>
              <Typography variant="h4">
                {slaData.atRiskTicketsCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Approaching deadline
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <TrendingUpIcon color="error" />
                <Typography variant="subtitle2" color="text.secondary">
                  SLA Breach Rate
                </Typography>
              </Box>
              <Typography variant="h4">
                {slaData.totalTickets > 0
                  ? (100 - slaData.overallComplianceRate).toFixed(1)
                  : 0}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {slaData.responseSlaBreaches + slaData.resolutionSlaBreaches} breaches across {slaData.totalTickets} tickets
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <TrendingUpIcon color="info" />
                <Typography variant="subtitle2" color="text.secondary">
                  Total Escalations
                </Typography>
              </Box>
              <Typography variant="h4">
                {escalationData.totalEscalations}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Rate: {escalationData.escalationRate.toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* At-Risk Tickets DataGrid */}
      {slaData.atRiskTicketsList.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              At-Risk Tickets (Approaching SLA Deadline)
            </Typography>
            <Box sx={{ height: 400 }}>
              <DataGrid
                rows={slaData.atRiskTicketsList}
                columns={atRiskColumns}
                getRowId={(row) => row.ticketId}
                initialState={{
                  pagination: { paginationModel: { pageSize: 10 } }
                }}
                pageSizeOptions={[10, 25, 50]}
                disableRowSelectionOnClick
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* SLA Breach Trends Chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            SLA Breach Trends (30 Days)
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={slaData.breachTrends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="responseBreaches"
                stroke="#ff9800"
                name="Response Breaches"
              />
              <Line
                type="monotone"
                dataKey="resolutionBreaches"
                stroke="#f44336"
                name="Resolution Breaches"
              />
              <Line
                type="monotone"
                dataKey="complianceRate"
                stroke="#4caf50"
                name="Compliance %"
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Escalation Frequency Chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Escalation Frequency Trends (30 Days)
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={escalationData.frequencyTrend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="manualEscalations"
                stroke="#2196f3"
                name="Manual"
              />
              <Line
                type="monotone"
                dataKey="autoSlaEscalations"
                stroke="#9c27b0"
                name="Auto-SLA"
              />
              <Line
                type="monotone"
                dataKey="totalEscalations"
                stroke="#607d8b"
                name="Total"
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Top Categories Causing Escalations */}
      {escalationData.topCategories.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Top Categories Causing Escalations
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={escalationData.topCategories}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="categoryName" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="escalationCount" fill="#f44336" name="Escalations" />
                <Bar dataKey="escalationRate" fill="#ff9800" name="Rate %" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Escalation Path Usage Stats */}
      {escalationData.pathUsage.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Escalation Path Usage Statistics
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={escalationData.pathUsage}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="pathName" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="timesTriggered" fill="#2196f3" name="Times Triggered" />
                <Bar dataKey="successRate" fill="#4caf50" name="Success Rate %" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </Box>
  )
}

export default SlaEscalationAnalytics
